import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ShowMoreTextProps {
    text: string;
    maxLines?: number;
}

/**
 * Component that displays text with a limited number of lines.
 * Shows "more" button to expand and "less" button to collapse.
 */
const ShowMoreText: React.FC<ShowMoreTextProps> = ({ text, maxLines = 3 }) => {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [shouldShowButton, setShouldShowButton] = useState<boolean>(false);
    const textRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!textRef.current) {
            return;
        }
        const element = textRef.current;
        const lineHeight = parseInt(getComputedStyle(element).lineHeight);
        const maxHeight = lineHeight * maxLines;
        if (element.scrollHeight > maxHeight) {
            setShouldShowButton(true);
        } else {
            setShouldShowButton(false);
        }
    }, [text, maxLines]);

    const toggleExpanded = (): void => {
        setIsExpanded(!isExpanded);
    };

    return (
        <div className="d-flex align-items-end">
            <div
                ref={textRef}
                style={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: isExpanded ? 'none' : maxLines,
                    WebkitBoxOrient: 'vertical',
                    maxHeight: isExpanded ? 'none' : `${maxLines * 1.5}em`,
                    transition: 'max-height 0.3s ease',
                }}
            >
                {text}
            </div>
            {shouldShowButton && (
                <button
                    className="btn btn-sm btn-primary fs-11"
                    onClick={toggleExpanded}
                    style={{
                        marginTop: '8px',
                        background: 'none',
                        border: 'none',
                        color: '#007bff',
                        cursor: 'pointer',
                        fontSize: '11px',
                        padding: '0',
                    }}
                >
                    {isExpanded ? t('less') : t('more')}
                </button>
            )}
        </div>
    );
};
export default React.memo(ShowMoreText);